import { DateTimePicker } from '@components/DateTimePicker'
import { countLabel } from '@/utils/plural'
import Modal from '@components/Modal'
import ConfirmationModal from '@components/ConfirmationModal'
import { useAuth } from '@context/AuthContext'
import { useDeleteFlight, usePatchFlight } from '@hooks'
import { FlightResponse, GetUserType } from '@orbit/api-types'
import {
	legDraftFromFlight,
	setLegArrivalTime,
	setLegDepartureTime,
	setLegField,
	toLegUpdatePayload,
	togglePassenger,
	validateLegEditDraft,
	type LegEditDraft,
} from '../flightForm'
import { fullMemberName, sortMembersWithCurrentFirst } from '@lib/members'
import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import {
	Button,
	ButtonGroup,
	ErrorText,
	FieldRow,
	FormDescription,
	FormGroup,
	FormInput,
	FormLabel,
	PassengerCheckbox,
	PassengerName,
	PassengerRowStyled,
	PassengersList,
	SummaryBox,
} from '../AddFlightModal/AddFlightModal.styles'
import { backgroundOrange } from '@/theme/colors'
import { Avatar } from '@components/Avatar'

interface EditLegModalProps {
	isOpen: boolean
	onClose: () => void
	tripId: string
	leg: FlightResponse
	members: GetUserType[]
	canDelete: boolean
}


const EditLegModal: React.FC<EditLegModalProps> = ({ isOpen, onClose, tripId, leg, members, canDelete }) => {
	const { user } = useAuth()
	const patchFlight = usePatchFlight()
	const deleteFlight = useDeleteFlight()

	const [draft, setDraft] = useState<LegEditDraft>(() => legDraftFromFlight(leg))
	const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)

	useEffect(() => {
		if (isOpen && leg) {
			setDraft(legDraftFromFlight(leg))
		}
	}, [isOpen, leg])

	const sortedMembers = useMemo(() => sortMembersWithCurrentFirst(members, user?.id), [members, user?.id])


	const isManual = leg.isManuallyAdded

	const validation = validateLegEditDraft(draft, isManual)
	const canSubmit = validation.valid && !patchFlight.isPending

	const handleSubmit = async () => {
		if (!canSubmit) return
		try {
			await patchFlight.mutateAsync({
				tripId,
				flightId: leg.id,
				data: toLegUpdatePayload(draft, isManual),
			})
			toast.success('Flight leg updated!')
			onClose()
		} catch {
			toast.error('Unable to update flight leg. Please try again.')
		}
	}

	const handleDelete = async () => {
		if (!canDelete) {
			toast.error('This is the only leg in the flight. Delete the flight instead.')
			return
		}
		try {
			await deleteFlight.mutateAsync({ tripId, flightId: leg.id })
			toast.success('Flight leg deleted')
			setIsDeleteConfirmOpen(false)
			onClose()
		} catch {
			toast.error('Unable to delete flight leg. Please try again.')
		}
	}

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={isManual ? 'Edit Flight Leg' : 'Edit Passengers'}
				size="large"
				backgroundColor={backgroundOrange}
				closeOnBackdropClick={false}
				onDelete={canDelete ? () => setIsDeleteConfirmOpen(true) : undefined}
				footer={
					<ButtonGroup>
						<Button type="button" variant="secondary" onClick={onClose}>
							Cancel
						</Button>
						<Button type="button" variant="primary" disabled={!canSubmit} onClick={handleSubmit}>
							{patchFlight.isPending ? 'Saving…' : 'Save'}
						</Button>
					</ButtonGroup>
				}
			>
				{isManual ? (
					<>
						<FieldRow>
							<FormGroup>
								<FormLabel htmlFor="edit-airline">Airline IATA</FormLabel>
								<FormInput
									id="edit-airline"
									type="text"
									value={draft.leg.airlineIata}
									onChange={e => setDraft(prev => setLegField(prev, 'airlineIata', e.target.value.toUpperCase()))}
									maxLength={3}
								/>
							</FormGroup>
							<FormGroup>
								<FormLabel htmlFor="edit-number">Flight Number</FormLabel>
								<FormInput
									id="edit-number"
									type="text"
									value={draft.leg.flightNumber}
									onChange={e => setDraft(prev => setLegField(prev, 'flightNumber', e.target.value))}
									maxLength={6}
								/>
							</FormGroup>
						</FieldRow>

						<FieldRow>
							<FormGroup>
								<FormLabel htmlFor="edit-dep-airport">Departure Airport</FormLabel>
								<FormInput
									id="edit-dep-airport"
									type="text"
									value={draft.leg.departureAirport}
									onChange={e => setDraft(prev => setLegField(prev, 'departureAirport', e.target.value.toUpperCase()))}
									maxLength={3}
								/>
							</FormGroup>
							<FormGroup>
								<FormLabel htmlFor="edit-arr-airport">Arrival Airport</FormLabel>
								<FormInput
									id="edit-arr-airport"
									type="text"
									value={draft.leg.arrivalAirport}
									onChange={e => setDraft(prev => setLegField(prev, 'arrivalAirport', e.target.value.toUpperCase()))}
									maxLength={3}
								/>
							</FormGroup>
						</FieldRow>

						<FieldRow>
							<FormGroup>
								<FormLabel htmlFor="edit-dep-time">Departure Time</FormLabel>
								<DateTimePicker
									id="edit-dep-time"
									value={draft.leg.departureTime}
									onChange={next => setDraft(prev => setLegDepartureTime(prev, next))}
									placeholder="Select departure"
									use24HourTime
								/>
							</FormGroup>
							<FormGroup>
								<FormLabel htmlFor="edit-arr-time">Arrival Time (Optional)</FormLabel>
								<DateTimePicker
									id="edit-arr-time"
									value={draft.leg.arrivalTime}
									onChange={next => setDraft(prev => setLegArrivalTime(prev, next))}
									placeholder="Select arrival"
									use24HourTime
								/>
							</FormGroup>
						</FieldRow>
					</>
				) : (
					<FormGroup>
						<FormDescription>
							Auto-imported flights can only edit passengers. To change flight details, delete this leg and add it manually.
						</FormDescription>
					</FormGroup>
				)}

				<FormGroup>
					<FormLabel>Passengers</FormLabel>
					<FormDescription>Select who is on this leg</FormDescription>
					<PassengersList>
						{sortedMembers.map(member => {
							const isSelected = draft.passengerIds.has(member.id)
							return (
								<PassengerRowStyled
									key={member.id}
									$isSelected={isSelected}
									onClick={() => setDraft(prev => togglePassenger(prev, member.id))}
								>
									<PassengerCheckbox $checked={isSelected} />
									<Avatar user={member} size="md" />
									<PassengerName>{fullMemberName(member, user?.id)}</PassengerName>
								</PassengerRowStyled>
							)
						})}
					</PassengersList>
					<SummaryBox>
						{validation.passengerError ? (
							<ErrorText>{validation.passengerError}</ErrorText>
						) : (
							`${countLabel(draft.passengerIds.size, 'passenger')} selected`
						)}
					</SummaryBox>
				</FormGroup>
			</Modal>

			<ConfirmationModal
				isOpen={isDeleteConfirmOpen}
				onClose={() => setIsDeleteConfirmOpen(false)}
				onConfirm={handleDelete}
				title="Delete Flight Leg"
				message="Are you sure you want to delete this flight leg? This cannot be undone."
				confirmText="Delete"
				cancelText="Cancel"
				variant="danger"
			/>
		</>
	)
}

export default EditLegModal
